import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  cardSubscriptionsState,
  pendingCouponRefreshesState,
  pendingRewardRefreshesState,
  transactionsQuery,
  vendorCardSubscriptionIdState,
  vendorMessagesState,
} from '../../../store';

import type { BaseDocument, Card, CardSubscription, Message, Transaction } from '@rewardopl/types';
import type { SetStateAction } from 'jotai';

type Action = 'append' | 'delete' | 'prepend' | 'update';

type BasePayload<T extends BaseDocument = BaseDocument> = {
  type: string;
  action: Omit<Action, 'delete'>;
  data: T;
};

type DeletePayload<T extends BaseDocument = BaseDocument> = {
  type: string;
  action: 'delete';
  data: Pick<T, '_id'>;
};

type RefreshPayload<T extends BaseDocument = BaseDocument> = {
  type: string;
  action: 'refresh';
  data: T['_id'];
};

type Payload<T extends BaseDocument = BaseDocument> =
  | BasePayload<T>
  | DeletePayload<T>
  | RefreshPayload<T>;

function isDeletePayload<T extends BaseDocument>(payload: Payload<T>): payload is DeletePayload<T> {
  return payload.action === 'delete';
}

function isRefreshPayload(payload: Payload): payload is RefreshPayload {
  return payload.action === 'refresh';
}

function handleAction<T extends BaseDocument>(
  setter: (nextValueOrSetter: SetStateAction<T[]>) => void,
  payload: Exclude<Payload<T>, RefreshPayload<T>>,
) {
  if (isDeletePayload(payload)) {
    const { data: nextData } = payload;

    setter((prevData) => prevData.filter((prevItem) => prevItem._id !== nextData._id));
    return;
  }

  const { action, data: nextData } = payload;

  function isSameDocument(prevItem: T) {
    return prevItem._id === nextData._id;
  }

  function replaceDocument(prevItem: T) {
    return isSameDocument(prevItem) ? nextData : prevItem;
  }

  switch (action) {
    case 'append':
      setter((prevData) =>
        prevData.find(isSameDocument) ? prevData.map(replaceDocument) : [...prevData, nextData],
      );
      break;
    case 'prepend':
      setter((prevData) =>
        prevData.find(isSameDocument) ? prevData.map(replaceDocument) : [nextData, ...prevData],
      );
      break;
    case 'update':
      setter((prevData) => prevData.map(replaceDocument));
      break;
    default:
    // Ignore unknown action types
  }
}

export default function WebsocketHandler() {
  const cardSubscriptionId = useAtomValue(vendorCardSubscriptionIdState);
  const setCardSubscriptions = useSetAtom(cardSubscriptionsState);
  const setMessages = useSetAtom(vendorMessagesState);
  const setPendingCouponRefreshesState = useSetAtom(pendingCouponRefreshesState);
  const setPendingRewardRefreshesState = useSetAtom(pendingRewardRefreshesState);
  const setTransactions = useSetAtom(transactionsQuery(cardSubscriptionId));

  useEffect(() => {
    let ws: WebSocket;

    function setupWebSocket() {
      ws = new WebSocket(`wss://${window.location.host}/ws`);

      ws.addEventListener('message', (event) => {
        const { data: rawPayload } = event;

        const payload = JSON.parse(rawPayload) as Payload;

        if (isRefreshPayload(payload)) {
          switch (payload.type) {
            case 'coupon':
              setPendingCouponRefreshesState((prev) => [
                ...prev,
                (payload as RefreshPayload<Card>).data,
              ]);
              break;
            case 'reward':
              setPendingRewardRefreshesState((prev) => [
                ...prev,
                (payload as RefreshPayload<Card>).data,
              ]);
              break;
            default:
            // Ignore unknown refresh payload types
          }
        } else {
          switch (payload.type) {
            case 'card_subscription':
              handleAction(
                setCardSubscriptions,
                payload as Exclude<Payload<CardSubscription>, RefreshPayload>,
              );
              break;
            case 'message':
              handleAction(setMessages, payload as Exclude<Payload<Message>, RefreshPayload>);
              break;
            case 'transaction':
              handleAction(
                setTransactions,
                payload as Exclude<Payload<Transaction>, RefreshPayload>,
              );
              break;
            default:
            // Ignore unknown payload types
          }
        }
      });

      ws.addEventListener('close', (event) => {
        if (!event.wasClean) {
          setTimeout(() => {
            setupWebSocket();
          }, 1000);
        }
      });
    }

    setupWebSocket();

    return () => {
      if (!ws) {
        return;
      }

      ws.close();
    };
  }, [
    setCardSubscriptions,
    setMessages,
    setPendingCouponRefreshesState,
    setPendingRewardRefreshesState,
    setTransactions,
  ]);

  return null;
}
